import type { FC, ReactNode } from 'react'
import type { Device, DeviceCategory, DeviceStatus } from '@/types'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { StatusDot, glass } from '@/components/datacenter/hud/primitives'
import CmsText from '@/components/datacenter/cms-text'

import {
    ArrowDownUp,
    MapPin,
    Search,
    X
} from 'lucide-react'

import {
    formatInteger,
    matchesSearch,
    thermalColor,
    toCategory,
    toStatus,
    unitRange
} from '@/lib/inventory'

import {
    CATEGORY_META,
    CATEGORY_ORDER,
    STATUS_META,
    STATUS_ORDER
} from '@/constants/inventory'

type SortKey = 'location' | 'hostname' | 'power' | 'temperature'

const SORTS: { key: SortKey, label: string }[] = [
    { key: 'location', label: 'Location' },
    { key: 'hostname', label: 'Hostname' },
    { key: 'power', label: 'Power draw' },
    { key: 'temperature', label: 'Temperature' }
]

const InventoryDrawer: FC = (): ReactNode => {
    const open = useDatacenterStore(state => state.inventoryOpen)
    const setOpen = useDatacenterStore(state => state.setInventoryOpen)
    const search = useDatacenterStore(state => state.search)
    const setSearch = useDatacenterStore(state => state.setSearch)
    const categoryFilter = useDatacenterStore(state => state.categoryFilter)
    const setCategoryFilter = useDatacenterStore(state => state.setCategoryFilter)
    const statusFilter = useDatacenterStore(state => state.statusFilter)
    const setStatusFilter = useDatacenterStore(state => state.setStatusFilter)
    const setHoveredDevice = useDatacenterStore(state => state.setHoveredDevice)
    const selectDevice = useDatacenterStore(state => state.selectDevice)
    const { inventory, rackById, stats } = useInventory()
    const [sort, setSort] = useState<SortKey>('location')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (!open) return

        const timer = window.setTimeout(() => inputRef.current?.focus(), 250)

        return () => window.clearTimeout(timer)
    }, [open])

    const rackOrder = useMemo(() => {
        const sorted = [...inventory.racks].sort((a, b) => a.row_label.localeCompare(b.row_label, undefined, { numeric: true }) || a.position - b.position)

        return new Map(sorted.map((rack, index) => [rack.id, index]))
    }, [inventory.racks])

    const statusCounts = useMemo(() => {
        const counts: Record<DeviceStatus, number> = { online: 0, warning: 0, critical: 0, maintenance: 0, offline: 0, unknown: 0 }

        for (const device of inventory.devices) {
            if (categoryFilter !== 'all' && toCategory(device.category) !== categoryFilter) continue

            counts[toStatus(device.status)] += 1
        }

        return counts
    }, [inventory.devices, categoryFilter])

    const rows = useMemo(() => {
        const filtered = inventory.devices.filter(device => {
            if (categoryFilter !== 'all' && toCategory(device.category) !== categoryFilter) return false
            if (statusFilter !== 'all' && toStatus(device.status) !== statusFilter) return false

            return matchesSearch(device, device.rack_id ? rackById.get(device.rack_id) : undefined, search)
        })

        const byLocation = (a: Device, b: Device) => {
            const ra = a.rack_id ? rackOrder.get(a.rack_id) ?? 9999 : 9999
            const rb = b.rack_id ? rackOrder.get(b.rack_id) ?? 9999 : 9999

            return ra - rb || b.u_position - a.u_position
        }

        return filtered.sort((a, b) => {
            switch (sort) {
                case 'hostname':
                    return a.hostname.localeCompare(b.hostname)
                case 'power':
                    return (b.power_w ?? -1) - (a.power_w ?? -1)
                case 'temperature':
                    return (b.temperature_c ?? -100) - (a.temperature_c ?? -100)
                default:
                    return byLocation(a, b)
            }
        })
    }, [inventory.devices, categoryFilter, statusFilter, search, sort, rackById, rackOrder])

    const close = () => {
        setOpen(false)
        setHoveredDevice(null)
    }

    const open3d = (device: Device) => {
        if (!device.rack_id) return

        close()
        selectDevice(device.id, device.rack_id)
    }

    const resetFilters = () => {
        setSearch('')
        setCategoryFilter('all')
        setStatusFilter('all')
    }

    const categories: (DeviceCategory | 'all')[] = ['all', ...CATEGORY_ORDER]

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        key='inventory-backdrop'
                        className='absolute inset-0 z-40 bg-[#02040a]/50 backdrop-blur-[2px]'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                        aria-hidden='true'
                    />
                    <motion.section
                        key='inventory'
                        id='inventory'
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='inventory-title'
                        className={cn(glass, 'absolute inset-x-0 bottom-0 z-50 flex h-[86dvh] flex-col rounded-b-none bg-[#060a13]/90 md:inset-x-4 md:h-[72dvh]')}
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', stiffness: 260, damping: 32 }}
                        onMouseLeave={() => setHoveredDevice(null)}
                    >
                        <div className='mx-auto mt-2 h-1 w-12 rounded-full bg-white/15' aria-hidden='true' />

                        <header className='flex flex-wrap items-center gap-3 border-b border-white/10 px-4 pb-4 pt-3 sm:px-6'>
                            <div className='mr-auto'>
                                <h2 id='inventory-title' className='font-heading text-xl font-semibold text-white'>
                                    Inventory
                                </h2>
                                <p className='text-xs text-slate-400'>
                                    <span className='font-mono text-slate-200'>{formatInteger(rows.length)}</span>
                                    <span> of </span>
                                    <span className='font-mono'>{formatInteger(stats.devices)}</span>
                                    <span> devices. Click a row to fly to it.</span>
                                </p>
                            </div>

                            <label className='relative order-last w-full sm:order-none sm:w-72'>
                                <span className='sr-only'>
                                    Search the inventory
                                </span>
                                <Search className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-500' aria-hidden='true' />
                                <input
                                    ref={inputRef}
                                    type='search'
                                    value={search}
                                    onChange={event => setSearch(event.target.value)}
                                    placeholder='Hostname, IP, model, serial or rack'
                                    className='h-10 w-full rounded-xl border border-white/10 bg-white/5 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-300/30'
                                />
                            </label>

                            <label className='relative flex items-center gap-2 text-xs text-slate-400'>
                                <ArrowDownUp className='size-3.5' aria-hidden='true' />
                                <span className='sr-only'>
                                    Sort by
                                </span>
                                <select
                                    value={sort}
                                    onChange={event => setSort(event.target.value as SortKey)}
                                    className='h-10 rounded-xl border border-white/10 bg-[#0b1220] px-3 text-sm text-slate-200 focus:border-cyan-300/60 focus:outline-none'
                                >
                                    {SORTS.map(option => (
                                        <option key={option.key} value={option.key}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <button
                                type='button'
                                onClick={close}
                                className='flex size-10 items-center justify-center rounded-xl border border-white/10 text-slate-300 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                                aria-label='Close the inventory'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        </header>

                        <div className='flex flex-wrap items-center gap-2 border-b border-white/10 px-4 py-3 sm:px-6'>
                            <div role='tablist' aria-label='Device category' className='flex flex-wrap gap-1 rounded-xl bg-white/5 p-1'>
                                {categories.map(category => {
                                    const active = categoryFilter === category
                                    const count = category === 'all' ? stats.devices : stats.byCategory[category]
                                    const color = category === 'all' ? '#e2e8f0' : CATEGORY_META[category].color

                                    return (
                                        <button
                                            key={category}
                                            type='button'
                                            role='tab'
                                            aria-selected={active}
                                            onClick={() => setCategoryFilter(category)}
                                            className={cn('relative rounded-lg px-3 py-1.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70', active ? 'text-slate-950' : 'text-slate-300 hover:text-white')}
                                        >
                                            {active && <motion.span layoutId='inventory-tab' className='absolute inset-0 rounded-lg' style={{ backgroundColor: color }} transition={{ type: 'spring', stiffness: 500, damping: 38 }} />}
                                            <span className='relative'>
                                                {category === 'all' ? 'All' : CATEGORY_META[category].plural}
                                            </span>
                                            <span className={cn('relative ml-1.5 font-mono', active ? 'text-slate-800' : 'text-slate-500')}>
                                                {count}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            <div className='no-scrollbar flex gap-1.5 overflow-x-auto'>
                                <button
                                    type='button'
                                    onClick={() => setStatusFilter('all')}
                                    aria-pressed={statusFilter === 'all'}
                                    className={cn('shrink-0 rounded-full border px-3 py-1.5 text-xs transition', statusFilter === 'all' ? 'border-white/30 bg-white/10 text-white' : 'border-white/10 text-slate-400 hover:text-white')}
                                >
                                    Any status
                                </button>
                                {STATUS_ORDER.filter(status => statusCounts[status] > 0).map(status => (
                                    <button
                                        key={status}
                                        type='button'
                                        onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                                        aria-pressed={statusFilter === status}
                                        className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition', statusFilter === status ? 'text-white' : 'border-white/10 text-slate-400 hover:text-white')}
                                        style={statusFilter === status ? { borderColor: `${STATUS_META[status].color}88`, backgroundColor: `${STATUS_META[status].color}1f` } : undefined}
                                    >
                                        <StatusDot status={status} />
                                        {STATUS_META[status].label}
                                        <span className='font-mono text-slate-500'>
                                            {statusCounts[status]}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className='min-h-0 flex-1 overflow-y-auto'>
                            {rows.length === 0 ? (
                                <div className='flex h-full flex-col items-center justify-center gap-3 p-10 text-center'>
                                    <Search className='size-8 text-slate-600' aria-hidden='true' />
                                    <p className='text-sm text-slate-300'>
                                        No devices match these filters.
                                    </p>
                                    <button type='button' onClick={resetFilters} className='rounded-full border border-white/15 px-4 py-2 text-sm text-white transition hover:bg-white/10'>
                                        Clear filters
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <table className='hidden w-full text-left text-sm md:table'>
                                        <thead className='sticky top-0 z-10 bg-[#070c16]/95 text-[10px] uppercase tracking-[0.14em] text-slate-500 backdrop-blur'>
                                            <tr>
                                                <th scope='col' className='px-6 py-2.5 font-medium'>Status</th>
                                                <th scope='col' className='px-3 py-2.5 font-medium'>Device</th>
                                                <th scope='col' className='px-3 py-2.5 font-medium'>Location</th>
                                                <th scope='col' className='px-3 py-2.5 font-medium'>Hardware</th>
                                                <th scope='col' className='px-3 py-2.5 font-medium'>IP address</th>
                                                <th scope='col' className='px-3 py-2.5 text-right font-medium'>Power</th>
                                                <th scope='col' className='px-6 py-2.5 text-right font-medium'>Temp</th>
                                            </tr>
                                        </thead>
                                        <tbody className='animate-in fade-in duration-500'>
                                            {rows.map(device => {
                                                const status = toStatus(device.status)
                                                const rack = device.rack_id ? rackById.get(device.rack_id) : undefined
                                                const color = CATEGORY_META[toCategory(device.category)].color

                                                return (
                                                    <tr
                                                        key={device.id}
                                                        onMouseEnter={() => setHoveredDevice(device.id)}
                                                        onClick={() => open3d(device)}
                                                        className={cn('group border-t border-white/[0.05] transition-colors hover:bg-cyan-300/[0.06]', rack ? 'cursor-pointer' : 'cursor-default')}
                                                    >
                                                        <td className='px-6 py-2.5'>
                                                            <span className='inline-flex items-center gap-2 text-xs text-slate-300'>
                                                                <StatusDot status={status} />
                                                                <CmsText table='devices' id={device.id} field='status' value={device.status} className='capitalize' />
                                                            </span>
                                                        </td>
                                                        <td className='px-3 py-2.5'>
                                                            <button
                                                                type='button'
                                                                onClick={event => {
                                                                    event.stopPropagation()
                                                                    open3d(device)
                                                                }}
                                                                onFocus={() => setHoveredDevice(device.id)}
                                                                disabled={!rack}
                                                                className='flex items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                                                            >
                                                                <span className='h-7 w-0.5 shrink-0 rounded-full' style={{ backgroundColor: color }} aria-hidden='true' />
                                                                <span className='min-w-0'>
                                                                    <CmsText table='devices' id={device.id} field='hostname' value={device.hostname} className='block font-mono text-[13px] text-white group-hover:text-cyan-200' />
                                                                    <CmsText table='devices' id={device.id} field='type' value={device.type} className='block text-xs text-slate-500' />
                                                                </span>
                                                            </button>
                                                        </td>
                                                        <td className='px-3 py-2.5 text-xs text-slate-300'>
                                                            {rack ? (
                                                                <span className='inline-flex items-center gap-1.5'>
                                                                    <MapPin className='size-3 text-slate-500' aria-hidden='true' />
                                                                    <CmsText table='racks' id={rack.id} field='code' value={rack.code} className='font-mono' />
                                                                    <span className='font-mono text-slate-500'>{unitRange(device)}</span>
                                                                </span>
                                                            ) : (
                                                                <span className='text-slate-500'>Not racked</span>
                                                            )}
                                                        </td>
                                                        <td className='px-3 py-2.5 text-xs'>
                                                            {device.vendor && <CmsText table='devices' id={device.id} field='vendor' value={device.vendor} className='block text-slate-300' />}
                                                            {device.model && <CmsText table='devices' id={device.id} field='model' value={device.model} className='block text-slate-500' />}
                                                        </td>
                                                        <td className='px-3 py-2.5 text-xs'>
                                                            {device.ip_address ? <CmsText table='devices' id={device.id} field='ip_address' value={device.ip_address} className='font-mono text-slate-300' /> : <span className='text-slate-600'>-</span>}
                                                        </td>
                                                        <td className='px-3 py-2.5 text-right font-mono text-xs text-slate-300'>
                                                            {device.power_w !== null ? <><span>{formatInteger(device.power_w)}</span><span className='text-slate-500'> W</span></> : '-'}
                                                        </td>
                                                        <td className='px-6 py-2.5 text-right font-mono text-xs'>
                                                            {device.temperature_c !== null ? (
                                                                <span style={{ color: thermalColor(device.temperature_c) }}>
                                                                    <span>{device.temperature_c.toFixed(1)}</span>
                                                                    <span className='text-slate-500'> °C</span>
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>

                                    <ul className='divide-y divide-white/[0.06] md:hidden'>
                                        {rows.map(device => {
                                            const status = toStatus(device.status)
                                            const rack = device.rack_id ? rackById.get(device.rack_id) : undefined

                                            return (
                                                <li key={device.id}>
                                                    <button
                                                        type='button'
                                                        onClick={() => open3d(device)}
                                                        disabled={!rack}
                                                        className='flex w-full items-center gap-3 px-4 py-3 text-left'
                                                    >
                                                        <StatusDot status={status} />
                                                        <span className='min-w-0 flex-1'>
                                                            <CmsText table='devices' id={device.id} field='hostname' value={device.hostname} className='block truncate font-mono text-[13px] text-white' />
                                                            <CmsText table='devices' id={device.id} field='type' value={device.type} className='block truncate text-xs text-slate-500' />
                                                        </span>
                                                        {rack && (
                                                            <span className='shrink-0 text-right text-xs'>
                                                                <CmsText table='racks' id={rack.id} field='code' value={rack.code} className='block font-mono text-slate-300' />
                                                                <span className='block font-mono text-slate-500'>{unitRange(device)}</span>
                                                            </span>
                                                        )}
                                                    </button>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </>
                            )}
                        </div>
                    </motion.section>
                </>
            )}
        </AnimatePresence>
    )
}

export default InventoryDrawer
